import type { SaludHttp } from './api';

/**
 * Lo que la tabla de rendimiento muestra por ruta, a partir de las filas que
 * llegan por ruta **y método**.
 *
 * Vive fuera del componente para poder probarlo: acá hay una decisión que se
 * equivoca fácil y en silencio —dos de estas tres columnas son tasas y la
 * tercera es un conteo, así que no se agregan igual— y una tabla que suma mal
 * se lee perfecto y miente.
 */
export interface SaludDeRuta {
  /** 5xx. Tasa: el máximo entre los métodos de la ruta. */
  servidor: number;
  /** 4xx no declarados. Tasa, también el máximo. */
  contrato: number;
  /** 4xx declarados como operación normal. **Conteo**, sumado. */
  negocio: number;
}

/**
 * Por ruta y no por ruta+método: la tabla de latencia agrupa así, y una ruta
 * que atiende GET y POST se lee mejor junta que partida en dos filas que dicen
 * casi lo mismo.
 *
 * Las tasas se agregan con el **máximo** y no con el promedio: si el POST de
 * una ruta falla el 40% de las veces y el GET nunca, promediar deja un 20% que
 * no le pasa a nadie y que además no cruza el umbral. El máximo dice «acá hay
 * algo roto», que es lo que hay que saber para decidir si mirar.
 *
 * `negocio` se **suma** porque es un conteo y no una tasa: la pregunta que
 * responde es «a cuánta gente le dijo que no este servicio», y esa se contesta
 * sumando, no tomando el máximo.
 */
export function agruparSaludPorRuta(filas: readonly SaludHttp[] | undefined): Map<string, SaludDeRuta> {
  const porRuta = new Map<string, SaludDeRuta>();
  for (const f of filas ?? []) {
    const previo = porRuta.get(f.ruta) ?? { servidor: 0, contrato: 0, negocio: 0 };
    porRuta.set(f.ruta, {
      servidor: Math.max(previo.servidor, f.tasa_servidor),
      contrato: Math.max(previo.contrato, f.tasa_contrato),
      negocio: previo.negocio + (f.negocio ?? 0),
    });
  }
  return porRuta;
}
